<template>
  <q-dialog v-model="showModal" persistent :maximized="$q.screen.lt.sm">
    <q-card class="amazon-connection-modal">
      <q-card-section class="modal-header">
        <div class="welcome-icon">👋</div>
        <h2 class="welcome-title">Welcome to UniQin.ai!</h2>
        <p class="welcome-subtitle">Let's connect your Amazon account to get started</p>
      </q-card-section>

      <q-card-section class="modal-body">
        <div class="benefits-section">
          <p class="benefits-intro">This allows us to:</p>
          <div class="benefits-list">
            <div class="benefit-item">
              <q-icon name="check_circle" color="positive" size="24px" />
              <span>Sync your products & inventory</span>
            </div>
            <div class="benefit-item">
              <q-icon name="check_circle" color="positive" size="24px" />
              <span>Track sales & performance automatically</span>
            </div>
            <div class="benefit-item">
              <q-icon name="check_circle" color="positive" size="24px" />
              <span>Optimize pricing in real-time</span>
            </div>
            <div class="benefit-item">
              <q-icon name="check_circle" color="positive" size="24px" />
              <span>Generate campaign reports daily</span>
            </div>
          </div>
        </div>

        <div class="time-estimate">
          <q-icon name="schedule" size="20px" />
          <span>Takes 2 minutes</span>
        </div>

        <div class="security-note">
          <q-icon name="lock" size="20px" />
          <span>Your credentials stay with Amazon. We use secure OAuth.</span>
        </div>
      </q-card-section>

      <q-card-actions class="modal-actions">
        <q-btn
          class="connect-btn"
          label="Connect Amazon Seller Central"
          color="primary"
          size="lg"
          unelevated
          @click="connectAmazon"
          :loading="connecting"
        />

        <a class="skip-link" @click="skipForNow">
          I'll do this later
        </a>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { Auth } from 'aws-amplify';
import axios from 'axios';

export default {
  name: 'AmazonConnectionModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    userEmail: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showModal: this.show,
      connecting: false
    };
  },
  watch: {
    show(newVal) {
      this.showModal = newVal;
    }
  },
  methods: {
    async connectAmazon() {
      this.connecting = true;

      try {
        // Track connection attempt
        if (this.$mixpanel) {
          this.$mixpanel.track('Amazon Connection Initiated', {
            UserEmail: this.userEmail,
            Source: 'Welcome Modal'
          });
        }

        // Save context for when user returns
        localStorage.setItem('onboarding_pending', 'true');
        localStorage.setItem('connection_initiated', Date.now());

        // Get Amazon OAuth configuration
        const amz_app_id = process.env.VUE_APP_AMZ_APP_ID || 'amzn1.sp.solution.220bca05-f442-4f7b-b14f-888f252964f3';
        const redirect_uri = encodeURIComponent(window.location.origin + '/amazonconnection');

        // Build OAuth URL
        const oauthUrl = `https://sellercentral.amazon.in/apps/authorize/consent?` +
          `application_id=${amz_app_id}&` +
          `version=beta&` +
          `redirect_uri=${redirect_uri}`;

        // Redirect to Amazon OAuth
        window.location.href = oauthUrl;

      } catch (error) {
        console.error('Error initiating Amazon connection:', error);
        this.connecting = false;

        this.$q.notify({
          type: 'negative',
          message: 'Failed to initiate connection. Please try again.'
        });

        if (this.$mixpanel) {
          this.$mixpanel.track('Amazon Connection Error', {
            UserEmail: this.userEmail,
            Error: error.message
          });
        }
      }
    },

    async skipForNow() {
      try {
        // Track skip action
        if (this.$mixpanel) {
          this.$mixpanel.track('Amazon Connection Skipped', {
            UserEmail: this.userEmail,
            Source: 'Welcome Modal'
          });
        }

        // Mark that user skipped connection
        localStorage.setItem('connection_skipped', 'true');
        localStorage.setItem('connection_skipped_at', Date.now());

        // Schedule reminder email
        await this.scheduleReminderEmail();

        // Emit event to parent
        this.$emit('skip');

        // Close modal
        this.showModal = false;

        // Redirect to dashboard
        this.$router.push({ name: 'SignIn' });

      } catch (error) {
        console.error('Error skipping connection:', error);

        // Still allow user to proceed even if reminder scheduling fails
        this.showModal = false;
        this.$router.push({ name: 'SignIn' });
      }
    },

    async scheduleReminderEmail() {
      try {
        const apiUrl = `${process.env.VUE_APP_PUBLIC_API}/seller-reauth/schedule-reminder`;

        await axios.post(apiUrl, {
          email: this.userEmail,
          type: 'connection_reminder',
          send_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours
        });

        console.log('Reminder email scheduled successfully');
      } catch (error) {
        // Don't throw error - this is not critical
        console.error('Failed to schedule reminder email:', error);
      }
    }
  }
};
</script>

<style scoped>
.amazon-connection-modal {
  max-width: 600px;
  border-radius: 16px;
  padding: 0;
}

.modal-header {
  text-align: center;
  padding: 40px 32px 24px;
  background: linear-gradient(135deg, #378CEF 0%, #2166B1 100%);
  color: white;
}

.welcome-icon {
  font-size: 64px;
  margin-bottom: 16px;
  animation: wave 1s ease-in-out;
}

@keyframes wave {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(20deg); }
  75% { transform: rotate(-20deg); }
}

.welcome-title {
  font-size: 28px;
  font-weight: bold;
  margin: 0 0 8px 0;
}

.welcome-subtitle {
  font-size: 16px;
  opacity: 0.95;
  margin: 0;
}

.modal-body {
  padding: 32px;
}

.benefits-section {
  margin-bottom: 24px;
}

.benefits-intro {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.benefits-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  color: #555;
}

.time-estimate {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #F0F7FF;
  border-radius: 8px;
  color: #2166B1;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 16px;
}

.security-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #F5F5F5;
  border-radius: 8px;
  color: #666;
  font-size: 13px;
}

.modal-actions {
  flex-direction: column;
  padding: 0 32px 32px;
  gap: 16px;
}

.connect-btn {
  width: 100%;
  height: 52px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  text-transform: none;
}

.skip-link {
  text-align: center;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;
  padding: 8px;
}

.skip-link:hover {
  color: #378CEF;
  text-decoration: underline;
}

/* Mobile responsive */
@media (max-width: 600px) {
  .modal-header {
    padding: 32px 24px 20px;
  }

  .welcome-icon {
    font-size: 48px;
  }

  .welcome-title {
    font-size: 24px;
  }

  .welcome-subtitle {
    font-size: 14px;
  }

  .modal-body {
    padding: 24px;
  }

  .modal-actions {
    padding: 0 24px 24px;
  }

  .connect-btn {
    height: 48px;
    font-size: 15px;
  }
}
</style>
